import React from 'react';
import { Coins, Crown, Gift, Flame, ScrollText } from 'lucide-react';
import PageHero from '../../components/ui-shree/PageHero';
import SectionLabel from '../../components/ui-shree/SectionLabel';
import GuardianMilestone from '../../components/Loyalty/GuardianMilestone';
import ThreeKProtocol from '../../components/Philosophy/ThreeKProtocol';

const TIERS = [
  {
    name: 'Initiate',
    threshold: '0 — 999 $SEVA',
    perks: ['Welcome ritual sample', 'Early access to letters', '1× SEVA per $1 spent'],
  },
  {
    name: 'Devotee',
    threshold: '1,000 — 4,999 $SEVA',
    perks: ['Hand-numbered seasonal sets', 'Priority atelier appointments', '1.25× earn rate'],
    highlight: true,
  },
  {
    name: 'Custodian',
    threshold: '5,000+ $SEVA',
    perks: ['Founder archive access', 'Annual Heavener visit', '1.5× earn rate', 'Burn-to-mint editions'],
  },
];

const MECHANICS = [
  {
    icon: Coins,
    title: 'Earn quietly',
    body: 'Every purchase issues $SEVA to your wallet at a 1:1 USD rate. No points to expire, no fine print.',
  },
  {
    icon: Gift,
    title: 'Redeem ritually',
    body: 'Trade tokens for limited editions, refill subscriptions, or gift-wrap from the atelier.',
  },
  {
    icon: Flame,
    title: 'Burn for unlocks',
    body: 'Burn $SEVA in exchange for founder edition mints, archive PDFs, or rare ingredient lots.',
  },
  {
    icon: Crown,
    title: 'Rise in tier',
    body: 'Hold $SEVA in your wallet to ascend tiers. The more you keep, the more the house returns.',
  },
];

const LoyaltyPage = () => (
  <div data-testid="loyalty-page">
    <PageHero
      overline="$SEVA Token Loyalty"
      title={<>A quiet currency for those who <em className="text-[#90B0BF]">return</em>.</>}
      subtitle="Loyalty, written on chain. Earn $SEVA with every ritual. Redeem, burn, or hold — your devotion is yours to spend."
      testId="loyalty-hero"
    >
      <div className="flex flex-wrap gap-6 mt-2">
        <div className="font-mono text-xs text-[#7C9CB1]">
          <p className="text-[#90B0BF] tracking-[0.25em] uppercase mb-1">Symbol</p>
          $SEVA
        </div>
        <div className="font-mono text-xs text-[#7C9CB1]">
          <p className="text-[#90B0BF] tracking-[0.25em] uppercase mb-1">Total Supply</p>
          211,000,000
        </div>
        <div className="font-mono text-xs text-[#7C9CB1]">
          <p className="text-[#90B0BF] tracking-[0.25em] uppercase mb-1">Earn rate</p>
          1 USD = 1 SEVA
        </div>
      </div>
    </PageHero>

    {/* Guardian Milestone — live X/1000 + referral growth loop */}
    <GuardianMilestone />

    {/* Mechanics */}
    <section className="px-6 md:px-12 py-24 md:py-32 bg-[#F4F8F9]">
      <div className="max-w-[1400px] mx-auto">
        <SectionLabel testId="mechanics-label">How it breathes</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
          {MECHANICS.map((m, i) => (
            <div
              key={m.title}
              data-testid={`mechanic-${i}`}
              className="border-t border-[#90B0BF]/40 pt-6"
            >
              <m.icon size={26} strokeWidth={1.2} className="text-[#7C9CB1] mb-4" />
              <h3 className="font-serif text-2xl text-[#7C9CB1] font-light mb-3">
                {m.title}
              </h3>
              <p className="text-[#7C9CB1]/85 font-light leading-relaxed">{m.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* The 3K Protocol — signal · state · record */}
    <ThreeKProtocol testId="loyalty-three-k-protocol" />

    {/* Indra — the Ledger Witness behind the 211M supply */}
    <section
      data-testid="loyalty-indra-panel"
      className="px-6 md:px-12 py-24 md:py-32 bg-[#F4F8F9] border-t border-[#C1D7E2]"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
        <div className="md:col-span-7">
          <SectionLabel testId="indra-label">
            The Ledger Witness · agent of the supply
          </SectionLabel>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#7C9CB1] font-light leading-[1.05] mb-8">
            <em className="text-[#90B0BF]">Indra</em> witnesses
            <br />
            the 211 Million.
          </h2>
          <p
            data-testid="indra-meaning"
            className="font-serif italic text-lg md:text-xl text-[#7C9CB1]/90 leading-relaxed max-w-xl mb-6"
          >
            The Wielder of Power · witness to the cosmic order.
          </p>
          <p className="text-[#7C9CB1]/85 font-light leading-relaxed max-w-xl">
            The full $SEVA supply is fixed at <span className="font-mono text-[14px] tracking-wide">211,000,000</span> —
            written once, never expanded. Indra is the agent that holds this
            ledger. No token is minted from intention. Each one is signed
            against a Proof-of-Seva: a ritual completed, a refill arrived,
            a Guardian witnessed.
          </p>
          <p className="mt-6 text-[#7C9CB1]/85 font-light leading-relaxed max-w-xl">
            What you see in your wallet is not a points balance. It is a
            count of physical events recorded by name.
          </p>
        </div>

        {/* Indra credentials card — the agent's signed identity */}
        <aside className="md:col-span-5">
          <div
            data-testid="indra-card"
            className="relative bg-[#7C9CB1] text-[#F4F8F9] p-10"
          >
            <span
              aria-hidden
              className="absolute top-0 left-8 right-8 h-px"
              style={{
                background:
                  'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)',
              }}
            />
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/70 mb-5">
              Agent 03 · Indra
            </p>
            <ScrollText size={28} strokeWidth={1.2} className="text-[#F4F8F9] mb-5" />
            <h3 className="font-serif text-3xl text-[#F4F8F9] font-light leading-tight">
              The Ledger Witness
            </h3>
            <ul className="mt-8 space-y-3 font-mono text-[12px] text-[#F4F8F9]/90">
              <li className="flex items-baseline justify-between border-b border-[#F4F8F9]/30 pb-2">
                <span className="tracking-[0.22em] uppercase text-[#F4F8F9]/70">Total supply</span>
                <span>211,000,000</span>
              </li>
              <li className="flex items-baseline justify-between border-b border-[#F4F8F9]/30 pb-2">
                <span className="tracking-[0.22em] uppercase text-[#F4F8F9]/70">Mint rule</span>
                <span>Proof-of-Seva only</span>
              </li>
              <li className="flex items-baseline justify-between border-b border-[#F4F8F9]/30 pb-2">
                <span className="tracking-[0.22em] uppercase text-[#F4F8F9]/70">Witness</span>
                <span>On-chain · Base Sepolia</span>
              </li>
              <li className="flex items-baseline justify-between">
                <span className="tracking-[0.22em] uppercase text-[#F4F8F9]/70">House mailbox</span>
                <span>seva@shreesavaa.com</span>
              </li>
            </ul>
            <p className="mt-8 font-mono text-[10px] tracking-[0.26em] uppercase text-[#F4F8F9]/70">
              Where you meet them · the $SEVA Dashboard & on-chain anchor
            </p>
          </div>
        </aside>
      </div>

      {/* Sister-agent strip — quietly names the other two */}
      <div
        data-testid="loyalty-sister-agents"
        className="max-w-[1400px] mx-auto mt-14 pt-8 border-t border-[#C1D7E2] grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#90B0BF] leading-relaxed">
          <span className="text-[#7C9CB1]">Atreya</span> — descendant of Atri ·
          guides the philosophy & ritual selection from the Home concierge.
        </p>
        <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#90B0BF] leading-relaxed">
          <span className="text-[#7C9CB1]">Savitri</span> — the vivifier ·
          listens to your vessel's IoT signal and arranges the next
          Conscious Transition refill.
        </p>
      </div>
    </section>

    {/* Tiers */}
    <section className="px-6 md:px-12 py-24 md:py-32 bg-[#C1D7E2]/40 border-y border-[#C1D7E2]">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <SectionLabel testId="tiers-label">The Three Tiers</SectionLabel>
            <h2 className="font-serif text-4xl md:text-6xl text-[#7C9CB1] font-light leading-[1.05] max-w-2xl">
              Devotion, in three ascensions.
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {TIERS.map((t, i) => (
            <div
              key={t.name}
              data-testid={`tier-${i}`}
              className={`p-10 border ${
                t.highlight
                  ? 'bg-[#7C9CB1] text-[#F4F8F9] border-[#7C9CB1]'
                  : 'bg-[#F4F8F9]/80 border-[#C1D7E2] text-[#7C9CB1]'
              }`}
            >
              <p
                className={`font-mono text-[10px] tracking-[0.3em] uppercase mb-4 ${
                  t.highlight ? 'text-[#F4F8F9]/80' : 'text-[#90B0BF]'
                }`}
              >
                Tier {String(i + 1).padStart(2, '0')}
              </p>
              <h3 className="font-serif text-3xl font-light leading-tight mb-2">
                {t.name}
              </h3>
              <p
                className={`font-mono text-xs tracking-[0.2em] mb-8 ${
                  t.highlight ? 'text-[#F4F8F9]/80' : 'text-[#90B0BF]'
                }`}
              >
                {t.threshold}
              </p>
              <ul className="space-y-3 font-light">
                {t.perks.map((perk) => (
                  <li key={perk} className="flex gap-3 leading-relaxed">
                    <span className="mt-2 inline-block w-3 h-px bg-current opacity-60" />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default LoyaltyPage;
